import { HttpErrorResponse } from '@angular/common/http';
import { ApolloLink, FetchResult, NextLink, Observable, Operation } from '@apollo/client/core';
import { ObservableSubscription } from '@apollo/client/utilities/observables/Observable';
import { OperationDefinitionNode } from 'graphql';
import { print } from 'graphql/language/printer';
import { CommandResult, CommandResultStatus } from 'src/graphql/generated/graphql.generated';
import { AppInsightsService } from 'src/lib/tcs-app-insights/app-insights-initializer.service';
import { createSafeProps } from 'src/lib/tcs-app-insights/app-insights-util';

export class AppInsightsDependencyTrackingLink extends ApolloLink {
  constructor(
    private readonly _graphQLUrl: string,
    private _appInsightsService: AppInsightsService,
  ) {
    super();
  }

  public request(operation: Operation, forward: NextLink): Observable<FetchResult> | null {
    return new Observable((observer) => {
      let sub: ObservableSubscription | undefined;
      const begin = performance.now();
      const operationId: string | null = operation.getContext().operationId;
      const opDefs: OperationDefinitionNode[] = operation.query.definitions
        .filter((d) => d.kind === 'OperationDefinition')
        .map((n) => n as OperationDefinitionNode);
      const graphQLOperationType =
        opDefs.length > 1 ? 'multiple' : opDefs.length === 1 ? opDefs[0].operation : 'Multiple';
      const operationName = graphQLOperationType + ' ' + operation.operationName;
      if (operationId == null) {
        throw new Error('Expected non-null operation id');
      }
      const getDuration = () => performance.now() - begin;
      const baseProps = {
        __trackMe: true,
        variables: operation.variables,
        gqlOperationType: graphQLOperationType,
        gqlOperationName: operation.operationName,
        query: print(operation.query),
      };

      try {
        sub = forward(operation).subscribe({
          next: (result) => {
            const duration = getDuration();
            const hasErrors = result.errors != null && result.errors.length > 0;
            let resultProps = createSafeProps({
              ...baseProps,
              resultData: result.data,
              resultErrors: result.errors,
              hasErrors,
            });
            let resultCode = hasErrors ? 5000 : 200;
            let mutationErrorSource: undefined | 'user' | 'system';
            if (graphQLOperationType === 'mutation') {
              if (result != null && result.data != null) {
                const mutationResults = Object.values(result.data).filter(
                  (r) => r != null,
                ) as CommandResult[];
                const firstMutationResult = mutationResults.find((r) => r != null);
                resultProps = createSafeProps({
                  ...resultProps,
                  mutationSuccess: true,
                  mutationResult: firstMutationResult,
                  mutationResultStatus:
                    firstMutationResult != null ? firstMutationResult.status : undefined,
                });
                const firstMutationWithError = mutationResults.find(
                  (r) =>
                    r.status !== CommandResultStatus.Succeeded ||
                    (r.errors != null && r.errors.length > 0),
                );
                if (firstMutationWithError != null) {
                  const firstError = firstMutationWithError.errors[0];
                  resultCode = firstError.code;
                  mutationErrorSource =
                    firstError.code >= 4000 && firstError.code < 5000 ? 'user' : 'system';

                  resultProps = createSafeProps({
                    ...resultProps,
                    mutationSuccess: false,
                    mutationErrorMessage: firstError.message,
                    mutationErrorCode: firstError.code,
                    mutationErrorSource,
                  });
                }
              }
            }

            const success = mutationErrorSource !== 'system' && !hasErrors;
            this._appInsightsService?.appInsights?.trackDependencyData({
              id: operationId,
              name: 'GraphQL',
              target: this._graphQLUrl,
              duration,
              success,
              responseCode: resultCode,
              properties: { operationName, ...resultProps, operationId },
            });

            observer.next(result);
          },
          error: (response: HttpErrorResponse) => {
            const duration = getDuration();
            const errorProps = createSafeProps({
              ...baseProps,
              response,
              statusText: response.statusText,
            });
            this._appInsightsService?.appInsights?.trackDependencyData({
              id: operationId,
              name: 'GraphQL',
              target: this._graphQLUrl,
              duration,
              success: false,
              responseCode: response.status,
              properties: { operationName, ...errorProps, operationId },
            });
            observer.error(response);
          },
          complete: () => {
            observer.complete();
          },
        });
      } catch (err) {
        observer.error(err);
      }
      return () => {
        if (sub != null) {
          sub.unsubscribe();
        }
      };
    });
  }
}
